import type { ReactElement } from 'react';

import classNames from 'classnames';
import omit from 'lodash/omit';

import style from './Nav.module.css';

import type { NavigationItem } from '../../../NavItem';
import { NavItem } from '../../../NavItem';
import { Text } from '../../../Text';
import type { NavItemClickHandler, NavItemTrackingData } from '../../types';
import type { AppNavItem } from '../types';

interface NavProps {
  logo: ReactElement;
  leftItems: AppNavItem[];
  rightItems: AppNavItem[];
  showVisibleRightItems?: boolean;
  ctaEl?: React.ReactElement | undefined;
  disableResponsiveLayout?: boolean;
  menuButton?: ReactElement | undefined;
  leftItemsClassName?: string;
  rightItemsClassName?: string;
  onNavItemClick?: NavItemClickHandler;
}

/** Render the main navigation with logo, left/right items, etc. */
export function Nav(props: NavProps) {
  const {
    ctaEl,
    leftItems,
    rightItems,
    disableResponsiveLayout,
    showVisibleRightItems,
    leftItemsClassName,
    rightItemsClassName,
    onNavItemClick,
  } = props;

  const handleNavClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!onNavItemClick) {
      return;
    }

    const target = event.target as HTMLElement;

    const anchorElement = target.closest('a');
    if (!anchorElement) {
      return;
    }

    const label = anchorElement.textContent?.trim();
    const isLogoClick = label === '';

    const trackingData: NavItemTrackingData = {
      text: label,
      module_name: 'nav_bar',
      type: 'button',
      ...(isLogoClick ? { function: 'homepage_icon' } : {}),
    };

    onNavItemClick(trackingData, event.nativeEvent);
  };

  const visibleRightItems = rightItems.filter(item => item.alwaysVisible);

  const hideLargeNav =
    leftItems.length === 0 &&
    rightItems.length === 0 &&
    !ctaEl &&
    !disableResponsiveLayout;

  return (
    <nav className={style.nav} onClickCapture={handleNavClick}>
      {ctaEl && <div className={style.cta}>{ctaEl}</div>}

      <span className={style.logo}>{props.logo}</span>

      {/* Large viewport: show left/right nav */}
      {!hideLargeNav && (
        <div
          className={classNames(style.largeNav, {
            [style.disableResponsiveLayout]: disableResponsiveLayout,
          })}
          role="list"
          aria-label="Navigation"
        >
          <div className={style.largeNavLeft} role="presentation">
            {leftItems
              .map(item => omit(item, 'alwaysVisible') as NavigationItem)
              .map((item, i) => (
                <Text key={i} role="listitem">
                  <NavItem
                    className={classNames(style.navItem, leftItemsClassName)}
                    {...item}
                  />
                </Text>
              ))}
          </div>
          {(rightItems.length > 0 || ctaEl) && (
            <div className={style.largeNavRight} role="presentation">
              {ctaEl && <span role="listitem">{ctaEl}</span>}
              {rightItems
                .map(item => omit(item, 'alwaysVisible') as NavigationItem)
                .map((item, i) => (
                  <Text key={i} role="listitem">
                    <NavItem
                      className={classNames(style.navItem, rightItemsClassName)}
                      {...item}
                    />
                  </Text>
                ))}
            </div>
          )}
        </div>
      )}

      {/* Small viewport: hide left (not right) nav and display a button for the menu */}
      {!disableResponsiveLayout && (
        <div className={style.smallNav}>
          {props.menuButton}
          {showVisibleRightItems && visibleRightItems.length > 0 && (
            <div role="list" aria-label="Navigation">
              {visibleRightItems
                .map(item => omit(item, 'alwaysVisible') as NavigationItem)
                .map((item, i) => (
                  <Text key={i} role="listitem">
                    <NavItem key={i} className={style.navItem} {...item} />
                  </Text>
                ))}
            </div>
          )}
        </div>
      )}
    </nav>
  );
}
